import React, { useEffect, useRef, useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { PencilIcon } from '../../../../../ui/icons/icons';

interface Box {
  id: string; // Changed to string for dnd compatibility
  label: string;
  isEditing?: boolean;
}

interface AddRemoveComponentProps {
  initialLabels: string[];
  onLabelsChange: (labels: string[]) => void;
  disabled?: boolean;
  editable?: boolean;
  title?: string;
  droppableId?: string; // For dnd
}

const AddRemoveComponent = ({
  initialLabels,
  onLabelsChange,
  disabled,
  editable,
  title,
  droppableId = 'default-droppable',
}: AddRemoveComponentProps) => {
  const { t } = useTranslation(NS.adminPages);
  const UI = ADMIN_PAGES_KEYS.jobOpenings.ui;
  const [boxes, setBoxes] = useState<Box[]>(() => {
    return initialLabels.map((label, index) => ({
      id: `${label}-${index}-${Date.now()}`,
      label,
      isEditing: false,
    }));
  });
  const [showInput, setShowInput] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (skipUpdateRef.current) {
      skipUpdateRef.current = false;
      return;
    }

    const labels = boxes.map((box) => box.label);
    onLabelsChange(labels);
  }, [boxes]);

  const firstRenderRef = useRef(true);
  const skipUpdateRef = useRef(false);

  useEffect(() => {
    if (!firstRenderRef.current) {
      const currentLabels = boxes.map((box) => box.label);
      const isSame =
        initialLabels.length === currentLabels.length &&
        initialLabels.every((label, index) => label === currentLabels[index]);

      if (isSame) return;

      skipUpdateRef.current = true;
      setBoxes(
        initialLabels.map((label, index) => ({
          id: `${label}-${index}`,
          label,
          isEditing: false,
        })),
      );
    }

    firstRenderRef.current = false;
  }, [initialLabels]);

  const handleAddBox = () => {
    if (inputValue.trim()) {
      const newBox: Box = {
        id: `${inputValue}-${boxes.length}-${Date.now()}`,
        label: inputValue,
        isEditing: false,
      };
      setBoxes([...boxes, newBox]);
      setInputValue('');
    }
    setShowInput(false);
  };

  const handleRemove = (id: string) => {
    setBoxes(boxes.filter((box) => box.id !== id));
  };

  const handleStartEdit = (id: string) => {
    setBoxes(
      boxes.map((box) => {
        if (box.id === id) {
          return { ...box, isEditing: true };
        }
        return { ...box, isEditing: false };
      }),
    );
  };

  const handleStopEdit = (id: string, newLabel: string) => {
    setBoxes((prevBoxes) =>
      prevBoxes.map((box) => {
        if (box.id === id) {
          return { ...box, label: newLabel, isEditing: false };
        }
        return box;
      }),
    );
  };

  const handleClickAdd = () => {
    setShowInput(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div className='w-full flex flex-col min-h-[150px]'>
      { /* Content Area */ }
      <Droppable droppableId={droppableId} direction='horizontal'>
        { (provided) => (
          <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className='flex-grow flex flex-wrap items-center gap-2.5 mb-4 min-h-[80px]'
          >
            { boxes.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-4 w-full border-2 border-dashed border-gray-100 rounded-xl'>
                <div className='w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center mb-2'>
                  <svg
                      className='w-5 h-5 text-gray-300'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 6v6m0 0v6m0-6h6m-6 0H6'
                    />
                  </svg>
                </div>
                <p className='text-xs font-medium text-gray-400'>
                  { editable ? t(UI.noItemsAvailable) : t(UI.noItemsAdded) }
                </p>
              </div>
            ) : (
              boxes.map((box, index) => (
                <Draggable
                    key={box.id}
                    draggableId={box.id}
                    index={index}
                    isDragDisabled={disabled || box.isEditing}
                >
                  { (provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`relative group flex items-center gap-2 ${
                          box.isEditing
                            ? 'bg-emerald-50 border-emerald-400 shadow-md ring-2 ring-emerald-100 z-50'
                            : snapshot.isDragging
                              ? 'bg-emerald-50 border-emerald-300 shadow-xl z-[100]'
                              : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-emerald-300 hover:shadow-sm transition-all duration-200'
                        } border rounded-lg py-2 px-3 min-w-[140px] max-w-full md:max-w-[300px] cursor-grab active:cursor-grabbing`}
                    >
                      { box.isEditing ? (
                        <input
                            type='text'
                            className='w-full px-1 py-0.5 bg-transparent outline-none text-xs font-medium text-gray-800'
                            defaultValue={box.label}
                            onBlur={(e) => handleStopEdit(box.id, e.target.value)}
                            onKeyDown={(e) =>
                              e.key === 'Enter' &&
                            handleStopEdit(box.id, e.currentTarget.value)
                            }
                            autoFocus
                        />
                      ) : (
                        <>
                          <div
                              className={`flex-1 ${
                                !disabled ? 'hover:text-emerald-600' : ''
                              } text-[11px] font-medium transition-colors duration-200 truncate`}
                              title={box.label}
                          >
                            { box.label }
                          </div>
                          { !disabled && (
                            <div className='flex items-center gap-1 shrink-0'>
                              <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartEdit(box.id);
                                  }}
                                  className='opacity-0 group-hover:opacity-100 hover:bg-emerald-50 p-1 rounded transition-all duration-200 text-gray-400 hover:text-emerald-500'
                                  title='Edit item'
                              >
                                <PencilIcon className='h-3 w-3' />
                              </button>
                              <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemove(box.id);
                                  }}
                                  className='opacity-0 group-hover:opacity-100 hover:bg-red-50 p-1 rounded transition-all duration-200 text-gray-400 hover:text-red-500'
                                  aria-label='Remove item'
                              >
                                <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='h-3 w-3'
                                    viewBox='0 0 20 20'
                                    fill='currentColor'
                                >
                                  <path
                                      fillRule='evenodd'
                                      d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                                      clipRule='evenodd'
                                  />
                                </svg>
                              </button>
                            </div>
                          ) }
                        </>
                      ) }
                    </div>
                  ) }
                </Draggable>
              ))
            ) }
            { provided.placeholder }
          </div>
        ) }
      </Droppable>

      { /* Input/Button Area */ }
      <div className='flex gap-2 mt-auto border-t border-gray-50 pt-3'>
        { showInput ? (
          <div className='flex-1 flex gap-2'>
            <input
                ref={inputRef}
                type='text'
                className='flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200'
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddBox()}
                onBlur={handleAddBox}
                placeholder={t(UI.typeSomething)}
            />
            <button
                onClick={() => setShowInput(false)}
                className='px-2 py-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors'
            >
              { t(UI.cancel) }
            </button>
          </div>
        ) : (
          !disabled && (
            <button
                onClick={handleClickAdd}
                className='flex items-center gap-1.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 font-semibold py-1.5 px-3 rounded-lg text-[11px] transition-all duration-200'
            >
              <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-3.5 w-3.5'
                  viewBox='0 0 20 20'
                  fill='currentColor'
              >
                <path
                    fillRule='evenodd'
                    d='M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z'
                    clipRule='evenodd'
                />
              </svg>
              { t(UI.addNew) }
            </button>
          )
        ) }
      </div>
    </div>
  );
};

export default AddRemoveComponent;
